import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock, Download, Users, Car, Mail, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_NAMES = {
  adler: "Adler (Heimat Edition)",
  falke: "Falke",
  quelle: "Quelle Supercar",
  ritter: "Ritter",
  koenig: "König",
  stein: "Stein",
  enz: "Enz",
  panzer: "Panzer"
};

// Animation variants
const fadeInAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 }
};

// Login Form Component
const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login?password=${encodeURIComponent(password)}`);
      onLogin(password);
      toast.success("Logged in successfully");
    } catch (error) {
      console.warn("Login failed:", error.message);
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  }, [password, onLogin]);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="admin-login">
      <div className="noise-overlay" />
      <motion.div
        initial={fadeInAnimation.initial}
        animate={fadeInAnimation.animate}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-display text-2xl font-bold uppercase">Admin Access</h1>
          <p className="text-sm text-muted-foreground mt-2">Enter password to view reservations</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            className="bg-secondary/50 border-white/10 focus:border-primary h-14 rounded-none"
            data-testid="admin-password-input"
          />
          <Button 
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground h-14 uppercase tracking-widest font-bold text-sm hover:bg-red-600"
            data-testid="admin-login-btn"
          >
            {loading ? "Checking..." : "Access Dashboard"}
          </Button>
        </form>
      </motion.div>
    </main>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-card border border-border p-6">
    <div className="flex items-center gap-3 mb-2">
      <Icon className="w-5 h-5 text-primary" />
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
    <p className="font-display text-4xl font-bold">{value}</p>
  </div>
);

// Stats Cards Component
const StatsCards = ({ stats }) => {
  if (!stats) return null;
  
  const otherVehicleCount = (stats.by_vehicle?.falke || 0) + 
    (stats.by_vehicle?.quelle || 0) + 
    (stats.by_vehicle?.ritter || 0) +
    (stats.by_vehicle?.koenig || 0) +
    (stats.by_vehicle?.stein || 0) +
    (stats.by_vehicle?.enz || 0) +
    (stats.by_vehicle?.panzer || 0);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <StatCard icon={Users} label="Total Reservations" value={stats.total_reservations} />
      <StatCard icon={Car} label="Adler" value={stats.by_vehicle?.adler || 0} />
      <StatCard icon={Car} label="Other Vehicles" value={otherVehicleCount} />
      <StatCard icon={Mail} label="Newsletter" value={stats.newsletter_subscribers} />
    </div>
  );
};

// Reservations Table Component
const ReservationsTable = ({ reservations }) => (
  <div className="border border-border overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="reservations-table">
        <thead className="bg-secondary/50">
          <tr>
            <th className="text-left text-xs uppercase tracking-wider p-4 font-medium">Date</th>
            <th className="text-left text-xs uppercase tracking-wider p-4 font-medium">Name</th>
            <th className="text-left text-xs uppercase tracking-wider p-4 font-medium">Email</th>
            <th className="text-left text-xs uppercase tracking-wider p-4 font-medium">Phone</th>
            <th className="text-left text-xs uppercase tracking-wider p-4 font-medium">Vehicle</th>
            <th className="text-left text-xs uppercase tracking-wider p-4 font-medium">Variant</th>
          </tr>
        </thead>
        <tbody>
          {reservations.length === 0 ? (
            <tr>
              <td colSpan={6} className="p-8 text-center text-muted-foreground">
                No reservations yet
              </td>
            </tr>
          ) : (
            reservations.map((r) => (
              <tr key={r.id} className="even:bg-card odd:bg-background">
                <td className="p-4 text-sm">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-sm font-medium">{r.first_name} {r.last_name}</td>
                <td className="p-4 text-sm text-primary">{r.email}</td>
                <td className="p-4 text-sm">{r.phone}</td>
                <td className="p-4 text-sm">{VEHICLE_NAMES[r.vehicle] || r.vehicle}</td>
                <td className="p-4 text-sm text-muted-foreground">{r.variant || "—"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Custom hook for admin data
const useAdminData = (password, isAuthenticated) => {
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    if (!password || !isAuthenticated) return;
    try {
      const encodedPassword = encodeURIComponent(password);
      const [resRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/reservations?password=${encodedPassword}`),
        axios.get(`${API}/admin/stats?password=${encodedPassword}`)
      ]);
      setReservations(resRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.warn("Failed to fetch admin data:", error.message);
      toast.error("Failed to fetch data");
    }
  }, [password, isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const clearData = useCallback(() => {
    setReservations([]);
    setStats(null);
  }, []);

  return { reservations, stats, fetchData, clearData };
};

// CSV Export hook
const useCSVExport = (reservations) => {
  return useCallback(() => {
    const headers = ["Date", "Name", "Email", "Phone", "Country", "Vehicle", "Variant", "Notes"];
    const rows = reservations.map(r => [
      new Date(r.created_at).toLocaleDateString(),
      `${r.first_name} ${r.last_name}`,
      r.email,
      r.phone,
      r.country || "",
      VEHICLE_NAMES[r.vehicle] || r.vehicle,
      r.variant || "",
      r.notes || ""
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `keller-motors-reservations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  }, [reservations]);
};

// Admin Dashboard Component
const AdminDashboard = ({ stats, reservations, onLogout, onExport }) => (
  <main className="min-h-screen bg-background" data-testid="admin-dashboard">
    <div className="noise-overlay" />
    
    {/* Header */}
    <header className="border-b border-border px-6 py-4">
      <div className="max-w-[1400px] mx-auto flex justify-between items-center">
        <div>
          <h1 className="font-display text-xl font-bold uppercase">Keller Motors Admin</h1>
          <p className="text-xs text-muted-foreground">Reservation Management</p>
        </div>
        <Button 
          variant="ghost" 
          onClick={onLogout}
          className="text-muted-foreground hover:text-foreground"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </header>

    <div className="max-w-[1400px] mx-auto px-6 py-8">
      <StatsCards stats={stats} />

      {/* Actions */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-lg font-bold uppercase">All Reservations</h2>
        <Button 
          onClick={onExport}
          className="bg-secondary text-foreground hover:bg-secondary/80"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <ReservationsTable reservations={reservations} />
    </div>
  </main>
);

// Main Admin Page Component
export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");

  // Check for existing session on mount
  useEffect(() => {
    const savedPass = sessionStorage.getItem("admin_session");
    if (savedPass) {
      setPassword(savedPass);
      setIsAuthenticated(true);
    }
  }, []);

  const { reservations, stats, clearData } = useAdminData(password, isAuthenticated);
  const exportCSV = useCSVExport(reservations);

  const handleLogin = useCallback((pass) => {
    setPassword(pass);
    setIsAuthenticated(true);
    sessionStorage.setItem("admin_session", pass);
  }, []);

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setPassword("");
    sessionStorage.removeItem("admin_session");
    clearData();
  }, [clearData]);

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <AdminDashboard 
      stats={stats}
      reservations={reservations}
      onLogout={handleLogout}
      onExport={exportCSV}
    />
  );
}
